import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoals } from '../hooks/useGoals';
import { GlassCard } from '../components/GlassCard';
import { GlassButton } from '../components/GlassButton';
import { ProgressBar } from '../components/ProgressBar';
import { DailyInputForm } from '../components/DailyInputForm';
import { CreateGoalModal } from '../components/CreateGoalModal';
import { ArrowLeft, Trash2, Edit } from 'lucide-react';
export function GoalDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    goals,
    addEntry,
    deleteGoal,
    updateGoal,
    calculateDailyTarget,
    calculateProgress,
    getTodayTotal,
    getTodayEntries
  } = useGoals();
  const [showEditModal, setShowEditModal] = useState(false);
  const goal = goals.find(g => g.id === id);
  if (!goal) {
    return <div className="min-h-screen flex items-center justify-center p-4" data-id="element-96">
        <GlassCard className="p-8 text-center" data-id="element-97">
          <p className="text-white text-xl mb-4" data-id="element-98">Goal not found</p>
          <GlassButton onClick={() => navigate('/')} variant="primary" data-id="element-99">
            Back to Dashboard
          </GlassButton>
        </GlassCard>
      </div>;
  }
  const progress = calculateProgress(goal);
  const dailyTarget = calculateDailyTarget(goal);
  const todayTotal = getTodayTotal(goal);
  const todayEntries = getTodayEntries(goal);
  const isComplete = progress >= 100;
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      deleteGoal(goal.id);
      navigate('/');
    }
  };
  const handleEdit = (updates: {
    name: string;
    type: 'debt' | 'savings';
    targetAmount: number;
    endDate: string;
  }) => {
    updateGoal(goal.id, updates);
  };
  // Group entries by date
  const entriesByDate = goal.entries.reduce((acc, entry) => {
    if (!acc[entry.date]) {
      acc[entry.date] = [];
    }
    acc[entry.date].push(entry);
    return acc;
  }, {} as Record<string, typeof goal.entries>);
  const sortedDates = Object.keys(entriesByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  return <div className="min-h-screen p-4 md:p-8" data-id="element-100">
      <div className="max-w-3xl mx-auto" data-id="element-101">
        <div className="flex items-center justify-between mb-8" data-id="element-102">
          <GlassButton onClick={() => navigate('/')} variant="secondary" data-id="element-103">
            <ArrowLeft size={20} className="inline mr-2" data-id="element-104" />
            Back
          </GlassButton>
          <div className="flex gap-3" data-id="element-105">
            <GlassButton onClick={() => setShowEditModal(true)} variant="secondary" data-id="element-106">
              <Edit size={20} data-id="element-107" />
            </GlassButton>
            <GlassButton onClick={handleDelete} variant="secondary" data-id="element-108">
              <Trash2 size={20} data-id="element-109" />
            </GlassButton>
          </div>
        </div>

        <GlassCard className="p-8 mb-6" data-id="element-110">
          <div className="flex items-start justify-between mb-6" data-id="element-111">
            <div data-id="element-112">
              <h1 className="text-3xl font-bold text-white mb-2" data-id="element-113">
                {goal.name}
              </h1>
              <span className="text-white/70 capitalize" data-id="element-114">{goal.type}</span>
            </div>
            {isComplete && <div className="text-center" data-id="element-115">
                <div className="text-4xl mb-1" data-id="element-116">🎉</div>
                <p className="text-sm text-white/80" data-id="element-117">Complete!</p>
              </div>}
          </div>

          <div className="space-y-6" data-id="element-118">
            <div data-id="element-119">
              <div className="flex justify-between text-sm text-white/70 mb-2" data-id="element-120">
                <span data-id="element-121">Progress</span>
                <span data-id="element-122">{progress.toFixed(1)}%</span>
              </div>
              <ProgressBar progress={progress} className="h-6" data-id="element-123" />
              <div className="flex justify-between text-white/80 mt-2" data-id="element-124">
                <span data-id="element-125">${goal.currentAmount.toLocaleString()}</span>
                <span data-id="element-126">${goal.targetAmount.toLocaleString()}</span>
              </div>
            </div>

            {!isComplete && <div className="pt-6 border-t border-white/20" data-id="element-127">
                <p className="text-white/70 text-sm mb-1" data-id="element-128">
                  Daily target to reach goal on time
                </p>
                <p className="text-4xl font-bold text-white" data-id="element-129">
                  ${dailyTarget.toFixed(2)}
                </p>
                {todayTotal > 0 && <p className="text-white/70 text-sm mt-2" data-id="element-130">
                    Today's total: ${todayTotal.toFixed(2)}
                  </p>}
              </div>}
          </div>
        </GlassCard>

        {!isComplete && <>
            <div className="mb-6" data-id="element-131">
              <DailyInputForm onSubmit={amount => addEntry(goal.id, amount)} data-id="element-132" />
            </div>

            {todayEntries.length > 0 && <GlassCard className="p-6 mb-6" data-id="element-133">
                <h3 className="text-xl font-semibold text-white mb-4" data-id="element-134">
                  Today's Contributions
                </h3>
                <div className="space-y-3" data-id="element-135">
                  {todayEntries.map(entry => <div key={entry.id} className="flex justify-between items-center py-3 border-b border-white/10 last:border-0" data-id="element-136">
                      <span className="text-white/70" data-id="element-137">
                        {new Date(entry.timestamp).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                      </span>
                      <span className="text-white font-semibold" data-id="element-138">
                        ${entry.amount.toFixed(2)}
                      </span>
                    </div>)}
                  <div className="flex justify-between items-center pt-3 border-t-2 border-white/30" data-id="element-139">
                    <span className="text-white font-semibold" data-id="element-140">
                      Total Today
                    </span>
                    <span className="text-white font-bold text-lg" data-id="element-141">
                      ${todayTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </GlassCard>}
          </>}

        <GlassCard className="p-6" data-id="element-142">
          <h3 className="text-xl font-semibold text-white mb-4" data-id="element-143">History</h3>
          {goal.entries.length === 0 ? <p className="text-white/60 text-center py-8" data-id="element-144">No entries yet</p> : <div className="space-y-4" data-id="element-145">
              {sortedDates.slice(0, 30).map(date => {
            const dateEntries = entriesByDate[date];
            const dateTotal = dateEntries.reduce((sum, e) => sum + e.amount, 0);
            return <div key={date} className="border-b border-white/10 last:border-0 pb-4 last:pb-0" data-id="element-146">
                    <div className="flex justify-between items-center mb-2" data-id="element-147">
                      <span className="text-white/90 font-medium" data-id="element-148">
                        {new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                      </span>
                      <span className="text-white font-semibold" data-id="element-149">
                        ${dateTotal.toFixed(2)}
                      </span>
                    </div>
                    {dateEntries.length > 1 && <div className="ml-4 space-y-1" data-id="element-150">
                        {dateEntries.sort((a, b) => b.timestamp - a.timestamp).map(entry => <div key={entry.id} className="flex justify-between text-sm text-white/60" data-id="element-151">
                              <span data-id="element-152">
                                {new Date(entry.timestamp).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                              </span>
                              <span data-id="element-153">${entry.amount.toFixed(2)}</span>
                            </div>)}
                      </div>}
                  </div>;
          })}
            </div>}
        </GlassCard>

        {showEditModal && <CreateGoalModal onClose={() => setShowEditModal(false)} onSubmit={handleEdit} editGoal={goal} data-id="element-154" />}
      </div>
    </div>;
}